import React, { Suspense, lazy, useEffect, useRef } from "react";
import { HashRouter as Router, Route, Switch } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import {
  faChevronCircleRight,
  faChevronCircleLeft,
  faChevronCircleUp,
  faCircle,
} from "@fortawesome/free-solid-svg-icons";
import "./App.scss";
import SensorsHome from "./pages/SensorsHome/SensorsHome";
import EmptyPageWhileLoading from "./pages/EmptyPageWhileLoading/EmptyPageWhileLoading";

export const AppContext = React.createContext();
const importPreloaded = (importPromise) => () => importPromise;

const CombustionEngine = lazy(
  importPreloaded(
    import("./pages/Powertrain/CombustionEngine/CombustionEngine")
  )
);

const CombustionEngineControl = lazy(
  importPreloaded(
    import("./pages/Powertrain/CombustionEngineControl/CombustionEngineControl")
  )
);

const ElectricPowertrainxEv = lazy(
  importPreloaded(
    import("./pages/Powertrain/ElectricPowertrainxEv/ElectricPowertrainxEv")
  )
);

const CurrentInterface = lazy(
  importPreloaded(
    import("./pages/Powertrain/CurrentInterface/CurrentInterface")
  )
);

const VoltageInterface = lazy(
  importPreloaded(
    import("./pages/Powertrain/VoltageInterface/VoltageInterface")
  )
);

const BrakeChassisControl = lazy(
  importPreloaded(
    import("./pages/Safety/BrakeChassisControl/BrakeChassisControl")
  )
);

const ElectricPowerSteering = lazy(
  importPreloaded(
    import("./pages/Safety/ElectricPowerSteering/ElectricPowerSteering")
  )
);

const Pedals = lazy(importPreloaded(import("./pages/Safety/Pedals/Pedals")));

const RadarBasedAssistance = lazy(
  importPreloaded(
    import("./pages/Safety/RadarBasedAssistance/RadarBasedAssistance")
  )
);

const AutomotiveMemsMicrophones = lazy(
  importPreloaded(
    import("./pages/Safety/AutomotiveMemsMicrophones/AutomotiveMemsMicrophones")
  )
);

const SabPedestrianProtection = lazy(
  importPreloaded(
    import("./pages/Safety/SabPedestrianProtection/SabPedestrianProtection")
  )
);

const SeatBeltPretensioner = lazy(
  importPreloaded(
    import("./pages/Safety/SeatBeltPretensioner/SeatBeltPretensioner")
  )
);

const TPMS = lazy(importPreloaded(import("./pages/Safety/TPMS/TPMS")));

const WheelSpeed = lazy(
  importPreloaded(import("./pages/Safety/WheelSpeed/WheelSpeed"))
);

const AdaptiveLighting = lazy(
  importPreloaded(
    import("./pages/BodyComfort/AdaptiveLighting/AdaptiveLighting")
  )
);

const ComfortDriving = lazy(
  importPreloaded(import("./pages/BodyComfort/ComfortDriving/ComfortDriving"))
);

const InCabinSoundSensing = lazy(
  importPreloaded(import("./pages/BodyComfort/InCabinSoundSensing/InCabinSoundSensing"))
);

const ControlElements = lazy(
  importPreloaded(import("./pages/BodyComfort/ControlElements/ControlElements"))
);

const SeatPosition = lazy(
  importPreloaded(import("./pages/BodyComfort/SeatPosition/SeatPosition"))
);

const TrunkGate = lazy(
  importPreloaded(import("./pages/BodyComfort/TrunkGate/TrunkGate"))
);

const WindowLiftSunroof = lazy(
  importPreloaded(
    import("./pages/BodyComfort/WindowLiftSunroof/WindowLiftSunroof")
  )
);

const Wipers = lazy(
  importPreloaded(import("./pages/BodyComfort/Wipers/Wipers"))
);

const IndustrialDrives = lazy(
  importPreloaded(import("./pages/IndustrialDrives/IndustrialDrives"))
);

const MotorPositionSensing = lazy(
  importPreloaded(
    import(
      "./pages/ConsumerApplications/MotorPositionSensing/MotorPositionSensing"
    )
  )
);

const Gimbals = lazy(
  importPreloaded(import("./pages/ConsumerApplications/Gimbals/Gimbals"))
);

const PositionDetection = lazy(
  importPreloaded(
    import("./pages/ConsumerApplications/PositionDetection/PositionDetection")
  )
);

const PowerTools = lazy(
  importPreloaded(import("./pages/ConsumerApplications/PowerTools/PowerTools"))
);

const PVInvertersModules = lazy(
  importPreloaded(
    import("./pages/ConsumerApplications/PVInvertersModules/PVInvertersModules")
  )
);

const SmartMetering = lazy(
  importPreloaded(
    import("./pages/ConsumerApplications/SmartMetering/SmartMetering")
  )
);

const SmartHVAC = lazy(
  importPreloaded(import("./pages/ConsumerApplications/SmartHVAC/SmartHVAC"))
);

const SmartLock = lazy(
  importPreloaded(import("./pages/ConsumerApplications/SmartLock/SmartLock"))
);

const SmartHomeDisplays = lazy(
  importPreloaded(import("./pages/ConsumerApplications/SmartHomeDisplays/SmartHomeDisplays"))
);

const SmartLighting = lazy(
  importPreloaded(
    import("./pages/ConsumerApplications/SmartLighting/SmartLighting")
  )
);

const SmartSpeaker = lazy(
  importPreloaded(
    import("./pages/ConsumerApplications/SmartSpeaker/SmartSpeaker")
  )
);

const WhiteGoods = lazy(
  importPreloaded(import("./pages/ConsumerApplications/WhiteGoods/WhiteGoods"))
);

const EBikes2WheelerCombustionEngine = lazy(
  importPreloaded(
    import("./pages/eBikes2Wheeler/CombustionEngine/CombustionEngine")
  )
);

const EBikes2WheelerCombustionEngineControl = lazy(
  importPreloaded(
    import(
      "./pages/eBikes2Wheeler/CombustionEngineControl/CombustionEngineControl"
    )
  )
);

const IndustrialRobotics = lazy(
  importPreloaded(
    import("./pages/Robotics/IndustrialRobotics/IndustrialRobotics")
  )
);

const PersonalServiceRobotics = lazy(
  importPreloaded(
    import("./pages/Robotics/PersonalServiceRobotics/PersonalServiceRobotics")
  )
);

const DronesControl = lazy(
  importPreloaded(import("./pages/Drones/DronesControl/DronesControl"))
);

const OvercurrentDetection = lazy(
  importPreloaded(
    import("./pages/Drones/DroneOvercurrentDetection/DroneOvercurrentDetection")
  )
);

const WearablesAndHearables = lazy(
  importPreloaded(import("./pages/WearablesAndHearables/WearablesAndHearables"))
);

const slideAnimationDurationMs = 1000;

const slideInTop = keyframes`
0% {
  transform: translate3d(0, -544px, 0);
}10% {
  transform: translate3d(0, -489.6px, 0);
}20% {
  transform: translate3d(0, -435.20000000000005px, 0);
}30% {
  transform: translate3d(0, -380.8px, 0);
}40% {
  transform: translate3d(0, -326.40000000000003px, 0);
}50% {
  transform: translate3d(0, -272px, 0);
}60% {
  transform: translate3d(0, -217.60000000000002px, 0);
}70% {
  transform: translate3d(0, -163.20000000000002px, 0);
}80% {
  transform: translate3d(0, -108.80000000000001px, 0);
}90% {
  transform: translate3d(0, -54.400000000000006px, 0);
}100% {
  transform: translate3d(0, 0px, 0);
}
`;
const slideOutTop = keyframes`
0% {
  transform: translate3d(0, 0px, 0);
}10% {
  transform: translate3d(0, -54.400000000000006px, 0);
}20% {
  transform: translate3d(0, -108.80000000000001px, 0);
}30% {
  transform: translate3d(0, -163.20000000000002px, 0);
}40% {
  transform: translate3d(0, -217.60000000000002px, 0);
}50% {
  transform: translate3d(0, -272px, 0);
}60% {
  transform: translate3d(0, -326.40000000000003px, 0);
}70% {
  transform: translate3d(0, -380.8px, 0);
}80% {
  transform: translate3d(0, -435.20000000000005px, 0);
}90% {
  transform: translate3d(0, -489.6px, 0);
}100% {
  transform: translate3d(0, -544px, 0);
}
`;

const slideInDown = keyframes`
0% {
  transform: translate3d(0, 544px, 0);
}10% {
  transform: translate3d(0, 489.6px, 0);
}20% {
  transform: translate3d(0, 435.20000000000005px, 0);
}30% {
  transform: translate3d(0, 380.8px, 0);
}40% {
  transform: translate3d(0, 326.40000000000003px, 0);
}50% {
  transform: translate3d(0, 272px, 0);
}60% {
  transform: translate3d(0, 217.60000000000002px, 0);
}70% {
  transform: translate3d(0, 163.20000000000002px, 0);
}80% {
  transform: translate3d(0, 108.80000000000001px, 0);
}90% {
  transform: translate3d(0, 54.400000000000006px, 0);
}100% {
  transform: translate3d(0, 0px, 0);
}
`;

const slideOutDown = keyframes`
0% {
  transform: translate3d(0, 0px, 0);
}10% {
  transform: translate3d(0, 54.400000000000006px, 0);
}20% {
  transform: translate3d(0, 108.80000000000001px, 0);
}30% {
  transform: translate3d(0, 163.20000000000002px, 0);
}40% {
  transform: translate3d(0, 217.60000000000002px, 0);
}50% {
  transform: translate3d(0, 272px, 0);
}60% {
  transform: translate3d(0, 326.40000000000003px, 0);
}70% {
  transform: translate3d(0, 380.8px, 0);
}80% {
  transform: translate3d(0, 435.20000000000005px, 0);
}90% {
  transform: translate3d(0, 489.6px, 0);
}100% {
  transform: translate3d(0, 544px, 0);
}
`;

const PageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  transition: height ${slideAnimationDurationMs / 1000}s;
`;

const Page = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const HomePageAnimationWrapper = styled(Page)`
  .page-enter & {
    animation: ${slideInTop} ${slideAnimationDurationMs / 1000}s forwards linear;
  }
  .page-exit & {
    animation: ${slideOutTop} ${slideAnimationDurationMs / 1000}s forwards
      linear;
  }
`;

export const DetailsPageAnimationWrapper = styled(Page)`
  .page-enter & {
    animation: ${slideInDown} ${slideAnimationDurationMs / 1000}s forwards
      linear;
  }
  .page-exit & {
    animation: ${slideOutDown} ${slideAnimationDurationMs / 1000}s forwards
      linear;
  }
`;

function App() {
  library.add(
    fas,
    faChevronCircleRight,
    faChevronCircleLeft,
    faChevronCircleUp,
    faCircle
  );
  const pageContainerRef = useRef(null);
  let pageWidth, pageHeight;
  const updatePageDimensions = (
    newPageWidth = undefined,
    newPageHeight = undefined,
    force = false
  ) => {
    pageWidth = newPageWidth || pageWidth;
    pageHeight = newPageHeight || pageHeight;
    if (!pageContainerRef.current) {
      return;
    }
    const pageContainerWidth = pageContainerRef.current.clientWidth;
    const pageContainerHeight =
      pageWidth && pageHeight && pageContainerWidth
        ? pageContainerWidth * (pageHeight / pageWidth) + "px"
        : "100vh";
    if (force) {
      pageContainerRef.current.classList.add("notransition");
    }
    pageContainerRef.current.style.height = pageContainerHeight;
    if (force) {
      setTimeout(() => {
        pageContainerRef.current.classList.remove("notransition");
      });
    }
  };

  useEffect(() => {
    const onResize = () => {
      updatePageDimensions(undefined, undefined, true);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  });

  return (
    <AppContext.Provider value={{ updatePageDimensions }}>
      <Router>
        <Route
          render={({ location }) => {
            return (
              <PageContainer ref={pageContainerRef}>
                <TransitionGroup component={null}>
                  <CSSTransition
                    timeout={slideAnimationDurationMs + 100}
                    classNames="page"
                    key={location.pathname}
                  >
                    <div>
                      <Suspense fallback={<EmptyPageWhileLoading />}>
                        <Switch location={location}>
                          <Route
                            path="/"
                            exact
                            component={() => (
                              <HomePageAnimationWrapper>
                                <SensorsHome
                                  updatePageDimensions={updatePageDimensions}
                                />
                              </HomePageAnimationWrapper>
                            )}
                          />
                          <Route
                            path="/combustion"
                            component={CombustionEngine}
                          />
                          <Route
                            exact
                            path="/combustion-engine-control"
                            component={CombustionEngineControl}
                          />
                          <Route
                            exact
                            path="/electric-powertrain-xev"
                            component={ElectricPowertrainxEv}
                          />
                          <Route
                            exact
                            path="/current-interface"
                            component={CurrentInterface}
                          />
                          <Route
                            exact
                            path="/voltage-interface"
                            component={VoltageInterface}
                          />
                          <Route
                            exact
                            path="/brake-chassis-control"
                            component={BrakeChassisControl}
                          />
                          <Route
                            exact
                            path="/electric-power-steering"
                            component={ElectricPowerSteering}
                          />
                          <Route exact path="/pedals" component={Pedals} />
                          <Route
                            exact
                            path="/radar-driver-assistance"
                            component={RadarBasedAssistance}
                          />
                          <Route
                            exact
                            path="/mems-microphones-for-automative"
                            component={AutomotiveMemsMicrophones}
                          />
                          <Route
                            exact
                            path="/in-cabin-sound-sensing"
                            component={InCabinSoundSensing}
                          />
                          <Route
                            exact
                            path="/sab"
                            component={SabPedestrianProtection}
                          />
                          <Route
                            exact
                            path="/seat-belt-pretensioner"
                            component={SeatBeltPretensioner}
                          />
                          <Route exact path="/tpms" component={TPMS} />
                          <Route
                            exact
                            path="/wheel-speed"
                            component={WheelSpeed}
                          />
                          <Route
                            exact
                            path="/adaptive-lighting"
                            component={AdaptiveLighting}
                          />
                          <Route
                            exact
                            path="/comfort-driving"
                            component={ComfortDriving}
                          />
                          <Route
                            exact
                            path="/control-elements"
                            component={ControlElements}
                          />
                          <Route
                            exact
                            path="/seat-position"
                            component={SeatPosition}
                          />
                          <Route
                            exact
                            path="/trunk-gate"
                            component={TrunkGate}
                          />
                          <Route
                            exact
                            path="/window-lift"
                            component={WindowLiftSunroof}
                          />
                          <Route exact path="/wipers" component={Wipers} />
                          <Route
                            exact
                            path="/industrial-drives"
                            component={IndustrialDrives}
                          />
                          <Route
                            exact
                            path="/motor-position-sensing"
                            component={MotorPositionSensing}
                          />
                          <Route exact path="/gimbals" component={Gimbals} />
                          <Route
                            exact
                            path="/position-detection"
                            component={PositionDetection}
                          />
                          <Route
                            exact
                            path="/power-tools"
                            component={PowerTools}
                          />
                          <Route
                            exact
                            path="/pv-inverters-modules"
                            component={PVInvertersModules}
                          />
                          <Route
                            exact
                            path="/smart-hvac"
                            component={SmartHVAC}
                          />
                          <Route
                            exact
                            path="/smart-lock"
                            component={SmartLock}
                          />
                          <Route
                            exact
                            path="/smart-home-displays"
                            component={SmartHomeDisplays}
                          />
                          <Route
                            exact
                            path="/smart-lighting"
                            component={SmartLighting}
                          />
                          <Route
                            exact
                            path="/smart-metering"
                            component={SmartMetering}
                          />
                          <Route
                            exact
                            path="/smart-speaker"
                            component={SmartSpeaker}
                          />
                          <Route
                            exact
                            path="/white-goods"
                            component={WhiteGoods}
                          />
                          <Route
                            exact
                            path="/2-wheeler-combustion-engine"
                            component={EBikes2WheelerCombustionEngine}
                          />
                          <Route
                            exact
                            path="/2-wheeler-combustion-engine-control"
                            component={EBikes2WheelerCombustionEngineControl}
                          />
                          <Route
                            exact
                            path="/industrial-robotics"
                            component={IndustrialRobotics}
                          />
                          <Route
                            exact
                            path="/personal-service-robotics"
                            component={PersonalServiceRobotics}
                          />
                          <Route
                            exact
                            path="/drones-control"
                            component={DronesControl}
                          />
                          <Route
                            exact
                            path="/drones-overcurrent-detection"
                            component={OvercurrentDetection}
                          />
                          <Route
                            exact
                            path="/wearables-and-hearables"
                            component={WearablesAndHearables}
                          />
                        </Switch>
                      </Suspense>
                    </div>
                  </CSSTransition>
                </TransitionGroup>
              </PageContainer>
            );
          }}
        />
      </Router>
    </AppContext.Provider>
  );
}

export default App;
